import React, { useEffect } from 'react';

import { Button, Form, Grid } from 'semantic-ui-react';
import { isEmpty } from 'lodash';
import { parse } from 'query-string';
import { useLocation } from '@reach/router';
import { FormikProps, withFormik } from 'formik';
import { navigate } from 'gatsby';

import Banner from 'src/components/shared/Banner';
import { getErrorMessage, hasAllValues, validateEmail } from 'src/utils/form-utils';

import Pvolve from '@pvolve/sdk';

import * as authStyles from 'src/styles/auth.module.scss';
import { SUI_GRID_CENTERED } from 'src/utils/semantic-utils';

interface FormValues {
    email: string;
    general?: string;
}

interface EmailFormProps {
    buttonLabel?: string;
    email?: string;
}

const EmailForm = ({
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    buttonLabel = 'CONTINUE WITH EMAIL',
    setFieldError,
}: EmailFormProps & FormikProps<FormValues>) => {
    const location = useLocation();
    const params = parse(location?.search);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (params?.error_description?.match(/Account already exists/i)) {
            setFieldError(
                'general',
                'Account with email already exists, please CONTINUE WITH EMAIL below.'
            );
        }
    }, []);

    const { widths } = SUI_GRID_CENTERED;

    return (
        <Form className={authStyles.emailForm} onSubmit={handleSubmit} noValidate>
            <Grid container centered>
                <Grid.Row>
                    <Grid.Column
                        mobile={widths.mobile}
                        tablet={widths.tablet}
                        computer={widths.computer}
                    >
                        <Form.Input
                            id="login-email"
                            name="email"
                            type="email"
                            label={{
                                children: 'Email',
                                htmlFor: 'login-email',
                                className: values.email ? '' : 'hidden',
                            }}
                            placeholder="Email"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.email}
                            error={
                                errors.email && touched.email
                                    ? getErrorMessage(errors.email)
                                    : false
                            }
                        />
                        {errors.general && <Banner type="ERROR">{errors.general}</Banner>}
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column
                        mobile={widths.mobile}
                        tablet={widths.tablet}
                        computer={widths.computer}
                    >
                        <Button
                            size="big"
                            type="submit"
                            disabled={isSubmitting || !hasAllValues(values) || !isEmpty(errors)}
                            primary
                            fluid
                            loading={isSubmitting}
                        >
                            {buttonLabel}
                        </Button>
                    </Grid.Column>
                </Grid.Row>
                <p className="tiny">
                    By continuing you agree to our{' '}
                    <a href="https://www.pvolve.com/pages/terms-of-service">terms of service</a>
                </p>
            </Grid>
        </Form>
    );
};

const FormikEmailForm = withFormik<EmailFormProps, FormValues>({
    handleSubmit: async (values: FormValues, actions) => {
        let user;

        // TODO move all of this to sagas
        try {
            user = await Pvolve.api.account.findUserByEmail(values.email);

            if (user?.status === 'CONFIRMED') {
                navigate('/login', {
                    state: {
                        email: values.email,
                        firstName: user.given_name,
                    },
                });
            } else if (user?.status === 'EXTERNAL_PROVIDER') {
                actions.setFieldError(
                    'general',
                    `You previously registered with ${user?.provider}. Please use ${user?.provider} to login.`
                );
            }
        } catch (error) {
            if (error?.response?.status === 404) {
                navigate('/signup', {
                    state: {
                        email: values.email,
                    },
                });
            } else {
                const errMsg = error?.message || 'Oops! Something went wrong. Please try again.';
                actions.setFieldError('general', errMsg);
            }
        }
    },
    validate: validateEmail,
    mapPropsToValues: (props) => ({
        email: props.email || '',
    }),
})(EmailForm);

export default FormikEmailForm;
